import React, { useState, useEffect, useCallback } from 'react';
import {
  Text,
  Link,
  LayoutServicePageState,
  useSitecoreContext,
  withDatasourceCheck,
  getFieldValue,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';

import type { RecordOfFields } from 'models/fields';
import classNames from 'classnames/bind';
import withStyles from 'lib/enhancers/withStyles';
import compose from 'lib/enhancers/compose';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import TextInput from 'components/helpers/TextInput/TextInput';
import Button from 'components/helpers/Button/Button';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';

import styles from './PolicyDocSearch.module.scss';
import type {
  Group,
  SubGroup,
  Document,
  DocumentDescription,
  PolicyDocSearchProps,
  GetPolicyDocumentsCallback,
} from '../../models/policy-doc-search-types';
import { SearchType } from '../../models/policy-doc-search-types';
import {
  getPolicyDocuments,
  parseDocumentDescription,
  getAllAlphabets,
  getDocumentGroupByDocGroup,
  getDocumentsCount,
} from './Functions';

const cx = classNames.bind(styles);

const PolicyDocSearch = (props: PolicyDocSearchProps): JSX.Element => {
  const { className, fields } = props;
  const { sitecoreContext } = useSitecoreContext();
  const { t } = useI18n();

  const [resultCountText, setResultCountText] = useState<string>('');
  const [searchTextBoxValue, setSearchTextBoxValue] = useState<string>('');
  const [showAllPoliciesLink, setShowAllPoliciesLink] = useState<boolean>(false);
  const [showNoResultsFound, setShowNoResultsFound] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [searchType, setSearchType] = useState<SearchType>(SearchType.SearchTitleOnly);
  const [documentsList, setDocumentsList] = useState<Group[]>([]);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  const {
    searchTitle,
    searchTitleOnlyLabel,
    searchDocumentTextLabel,
    searchResetButtonLabel,
    searchBoxPlaceholderText,
    disclaimer,
    searchButtonLabel,
    listLettersHeadingLevel,
    groupingTypeHeadingLevel,
    noResultsMessage,
    showMockData,
  } = fields;

  const listLettersHeadingLevelValue = getFieldValue(listLettersHeadingLevel?.fields as RecordOfFields, 'Value', 'h2');
  const groupingTypeHeadingLevelValue = getFieldValue(
    groupingTypeHeadingLevel?.fields as RecordOfFields,
    'Value',
    'h3'
  );

  const policyDocSearchTextId = 'policyDocSearchText';

  const getResultsCountText = (loading = true, documents: Group[] = []) => {
    let $resultCountContent = t('PolicyDocSearchResultsLoading');

    if (!loading) {
      const $count = getDocumentsCount(documents);
      $resultCountContent = '';

      if ($count > 0) {
        $resultCountContent =
          $count > 1 ? t('PolicyDocSearchResultsMultipleResults') : t('PolicyDocSearchResultsSingleResult');

        $resultCountContent = $resultCountContent.replace('{count}', $count.toString());
      }
    }

    return $resultCountContent;
  };

  const initializeSearchParamters = (searchTerm: string, loadAll: boolean) => {
    // Set the search controls state
    setShowLoader(true);
    setResultCountText(getResultsCountText());

    setDocumentsList([]);
    setShowAllPoliciesLink(searchTerm !== '');

    // Reset search box and radio buttons
    if (loadAll) {
      setSearchTextBoxValue('');
      setSearchType(SearchType.SearchTitleOnly);
    }
  };

  const updateDocumentsDataCallBack: GetPolicyDocumentsCallback = (documentsData: Group[]) => {
    setDocumentsList(documentsData || []);
    const $foundDocuments = documentsData && documentsData.length > 0;

    setResultCountText(getResultsCountText(false, documentsData));
    setShowNoResultsFound(!$foundDocuments);
    setShowLoader(false);
  };

  const handleSearchTypeClick = (type: SearchType) => {
    setSearchType(type);
  };

  const loadPolicyDocuments = useCallback(
    (loadAll = false) => {
      const mockData = showMockData?.value || sitecoreContext.pageState !== LayoutServicePageState.Normal;
      const searchTerm = loadAll ? '' : searchTextBoxValue.trim();
      initializeSearchParamters(searchTerm, loadAll);
      getPolicyDocuments(mockData, searchType, searchTerm, updateDocumentsDataCallBack);
    },
    [searchTextBoxValue, searchType, showMockData?.value]
  );

  useEffect(() => {
    // Just load it once - next loads will be controlled by search controls
    if (initialLoad) {
      setInitialLoad(false);
      loadPolicyDocuments();
    }
  }, [initialLoad, loadPolicyDocuments]);

  return (
    <div className={cx('policy-doc-search', className)}>
      <a id="glossary"></a>
      <div className={cx('policy-doc-search-container')}>
        <label htmlFor={policyDocSearchTextId} className={cx('search-box-label')}>
          <Text
            field={searchTitle}
            className={cx('text-purple-50', 'font-style-lg')}
            encode={sitecoreContext.pageEditing}
            editable={true}
          />
        </label>

        <div>
          <TextInput
            placeholder={searchBoxPlaceholderText?.value}
            id={policyDocSearchTextId}
            value={searchTextBoxValue}
            onChange={(e) => {
              setSearchTextBoxValue(e.target.value);
            }}
          />
        </div>

        <fieldset>
          <legend></legend>
          <ul className={cx('radio-list')}>
            <li role="group" aria-labelledby="searchType">
              <label>
                <input
                  type="radio"
                  name="searchRadioGroup"
                  value={SearchType.SearchTitleOnly}
                  title={searchTitleOnlyLabel?.value}
                  checked={searchType === SearchType.SearchTitleOnly}
                  onChange={() => {
                    handleSearchTypeClick(SearchType.SearchTitleOnly);
                  }}
                />
                <Text
                  field={searchTitleOnlyLabel}
                  tag="span"
                  aria-required="true"
                  encode={sitecoreContext.pageEditing}
                />
              </label>
            </li>
            <li>
              <label>
                <input
                  type="radio"
                  name="searchRadioGroup"
                  value={SearchType.SearchDocumentText}
                  title={searchDocumentTextLabel?.value}
                  checked={searchType === SearchType.SearchDocumentText}
                  onChange={() => {
                    handleSearchTypeClick(SearchType.SearchDocumentText);
                  }}
                />
                <Text
                  field={searchDocumentTextLabel}
                  tag="span"
                  aria-required="true"
                  encode={sitecoreContext.pageEditing}
                />
              </label>
            </li>
          </ul>
        </fieldset>

        <div className={cx('search-box')}>
          <Button
            className={cx('search-button')}
            type="button"
            disabled={!searchTextBoxValue.trim() || showLoader}
            onClick={() => {
              loadPolicyDocuments();
            }}
          >
            <Text field={searchButtonLabel} encode={sitecoreContext.pageEditing} />
          </Button>
        </div>

        {!showLoader && showAllPoliciesLink && (
          <Button
            className={cx('reset-search-button')}
            type="button"
            onClick={(e) => {
              loadPolicyDocuments(true);
              e.preventDefault();
            }}
          >
            <Text field={searchResetButtonLabel} encode={sitecoreContext.pageEditing} />
          </Button>
        )}

        <div className={cx('disclaimer')}>
          <RichTextWrapper field={disclaimer} editable={true} />
        </div>
      </div>

      {showLoader && (
        <div className={cx('loader-container')}>
          <div role="status" className="screen-reader-only">
            {resultCountText}
          </div>
          <Loader role="status" variant={LoaderVariant.Spinner} />
        </div>
      )}

      {!showLoader && showNoResultsFound && (
        <div className={cx('loader-container')} role="status">
          <RichTextWrapper field={noResultsMessage} editable={true} className={cx('no-records-message')} />
        </div>
      )}

      {!showLoader && !showNoResultsFound && resultCountText && (
        <div role="status" className={cx({ 'results-count': true, 'screen-reader-only': documentsList.length > 1 })}>
          {resultCountText}
        </div>
      )}

      {!showLoader && !showNoResultsFound && documentsList.length > 1 && (
        <div>
          <ul className={cx('rowlinks')}>
            {getAllAlphabets()?.map((alphabet: string, index: number) => {
              const group = getDocumentGroupByDocGroup(documentsList, alphabet);
              const $link = `#${alphabet}`;
              const $key = `group-${index}-${alphabet}`;

              return (
                <li key={$key}>
                  {(group.docGroup && (
                    <a href={$link} data-index={index}>
                      {alphabet}
                    </a>
                  )) || <span data-index={index}>{alphabet}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!showNoResultsFound &&
        documentsList?.length > 0 &&
        documentsList?.map((group: Group, index: number) => {
          const $link = `${group.docGroup}`;
          const $key = `detail-${index}-${group.docGroup}`;
          const $lettersHeadingClasses = cx('details-subheader', 'policy-info', 'text-purple-50');
          const $subGroups = group.subGroups;

          return (
            <div className={cx('box-details', 'policy')} key={$key}>
              <Text
                field={{ value: group.docGroup }}
                encode={false}
                editable={false}
                tag={listLettersHeadingLevelValue}
                className={$lettersHeadingClasses}
                id={$link}
              />

              {$subGroups?.map((subGroup: SubGroup, index: number) => {
                const { subHeaderDescription } = subGroup;
                const $subGroupKey = `${$key}-${index}`;
                const $groupingTypeHeading = cx('details-subheader', 'body-color', 'policy-info');
                const $documents = subGroup.policies;

                return (
                  <div key={$subGroupKey}>
                    <Text
                      field={{ value: subHeaderDescription }}
                      encode={false}
                      editable={false}
                      tag={groupingTypeHeadingLevelValue}
                      className={$groupingTypeHeading}
                    />

                    <ul>
                      {$documents?.map((document: Document, index: number) => {
                        const $documentKey = `${$subGroupKey}-${index}`;
                        const $documentDescription: DocumentDescription = parseDocumentDescription(
                          document.description
                        );

                        return (
                          <li key={$documentKey}>
                            <a href={document.link} target="_blank" className={cx('policy-doc-link')}>
                              <Text field={{ value: $documentDescription.title }} />
                            </a>
                            ({t('PolicyDocSearchEffectiveLabel')} {$documentDescription.effectiveDate})
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}

              <div className={cx('top-button', 'policy-info')}>
                <Link field={{ value: t('PolicyDocSearchBackToTop') }} href="#glossary">
                  <Text
                    encode={false}
                    field={{
                      value:
                        t('PolicyDocSearchBackToTop') +
                        ' <img src="/-/media/project/upmchealthplan/upmchealthplanwebsite/componenticons/caretup.svg" />',
                    }}
                  />
                </Link>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default compose<PolicyDocSearchProps>(withDatasourceCheck(), withStyles())(PolicyDocSearch);
